import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useEffect, useRef, useState } from "react";
import { appsPublicImg } from "../../../../../../../../../common/consts";
import ShurikenProgress from "../../../../../../../../shared/Animations/ShurikenProgress";
import {
    PrimaryButton,
    PrimaryButtonProps,
} from "../../../../../../../../shared/Button/PrimaryButton";
import { DarkPopoverMenu } from "../../../../../../../../shared/Popover/DarkPopoverMenu";
import { User } from "../../../../../../../../shared/User/types";
import { Item } from "../items/Item";
import { PossessedItem } from "../items/types";

const recordHeight = 100;
const imageContainerWidth = 80;
const imageWidth = 40;

export function Record({
    item,
    isNew: _isNew,
    isAdded: _isAdded,
    num,
    possessedItems,
    isFocused,
    user,
}: {
    item: Item;
    isNew: boolean;
    isAdded: boolean;
    num: number;
    possessedItems: PossessedItem[];
    isFocused: boolean;
    user: User;
}) {
    const [isNew, setNew] = useState(false);
    useEffect(() => {
        if (_isNew) {
            setNew(true);
        }
    }, [_isNew]);

    const [isAdded, setAdded] = useState(false);
    useEffect(() => {
        if (_isAdded) {
            setAdded(true);
        }
    }, [_isAdded]);

    const c = useStyles({ isFocused });

    return (
        <Card className={c.container}>
            <div className={c.leftBlock}>
                <div className={c.leftBlockInnerContainer}>
                    <img
                        src={`${appsPublicImg}item/${item.key}.png`}
                        className={c.image}
                    />
                    <div>× {num}</div>
                    {isAdded && !isNew && (
                        <Card className={c.added}>Added</Card>
                    )}
                </div>
            </div>
            <div className={c.rightBlock}>
                <div className={c.itemName}>{item.name}</div>
                {isNew && <Card className={c.new}>New</Card>}
                <div className={c.explanation}>{item.getExplanation(user)}</div>
                <ItemUseButton
                    item={item}
                    possessedItems={possessedItems}
                    itemCount={num}
                />
            </div>
        </Card>
    );
}
const useStyles = makeStyles<Theme, { isFocused: boolean }>(theme => ({
    container: ({ isFocused }) => ({
        marginBottom: 5,
        height: recordHeight,
        display: "flex",
        border: isFocused
            ? `solid 3px ${theme.palette.success.main}`
            : undefined,
    }),
    leftBlock: {
        height: recordHeight,
        width: imageContainerWidth,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    leftBlockInnerContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        lineHeight: 1.3,
    },
    image: { width: imageWidth },
    added: {
        backgroundColor: theme.palette.secondary.main,
        color: "white",
        padding: "0 5px",
        fontSize: "12px",
        position: "relative",
        top: -1,
    },
    rightBlock: { padding: 5, position: "relative", width: "100%" },
    itemName: {
        fontWeight: "bold",
        fontSize: "large",
        marginBottom: 2,
    },
    new: {
        position: "absolute",
        top: 6,
        right: 3,
        backgroundColor: theme.palette.secondary.main,
        transform: "rotate(20deg)",
        color: "white",
        padding: "0 5px",
        fontSize: "small",
    },
    explanation: {
        fontSize: "13px",
        color: theme.palette.grey[700],
        lineHeight: 1.4,
    },
}));

function ItemUseButton({
    item,
    possessedItems,
    itemCount,
}: {
    item: Item;
    possessedItems: PossessedItem[];
    itemCount: number;
}) {
    const [submitting, setSubmitting] = useState(false);
    const c = useItemUseButtonStyles();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isMenuOpen, setMenuOpen] = useState(false);

    const useItems = async (count: number) => {
        setSubmitting(true);
        await item.use(
            possessedItems.map(({ item }) => item.key),
            count
        );
        setSubmitting(false);
    };

    const buttonProps: PrimaryButtonProps = {
        className: c.button,
        size: "small",
        onClick: () => {
            if (itemCount === 1 || item.shouldUseOneByOne) {
                useItems(1);
                return;
            }
            setMenuOpen(true);
        },
        disabled: submitting,
        children: submitting ? <ShurikenProgress size="30%" /> : "Use",
        ref: buttonRef,
    };
    const button = item.UseButton ? (
        <item.UseButton {...buttonProps} />
    ) : (
        <PrimaryButton {...buttonProps} />
    );

    return (
        <div ref={containerRef}>
            {button}
            <DarkPopoverMenu
                anchorEl={buttonRef.current}
                containerEl={containerRef.current}
                open={isMenuOpen}
                onClose={() => {
                    setMenuOpen(false);
                }}
                items={[
                    {
                        label: "Use one",
                        onClick: () => {
                            useItems(1);
                        },
                    },
                    {
                        label: "Use all",
                        onClick: () => {
                            useItems(itemCount);
                        },
                    },
                ]}
                disabled={submitting}
            />
        </div>
    );
}
const useItemUseButtonStyles = makeStyles({
    button: {
        position: "absolute",
        bottom: 5,
        right: 5,
        maxWidth: 66,
        minWidth: 66,
        maxHeight: 30,
        minHeight: 30,
        fontSize: "15px !important",
        lineHeight: 1,
    },
});
